import { Button, buttonVariants } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useModal from '@/hooks/useModal';
import { retrieveCart } from '@/lib/actions/cart/action';
import { cn, formatIDR } from '@/lib/utils';
import { ThemeType } from '@/stores/builder';
import { HttpTypes } from '@/types';
import { BaseCart } from '@/types/http/cart/common';
import { useQuery } from '@tanstack/react-query';
import { XIcon, ShoppingBag } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HeaderElementDataType } from '@/components/sections/builder/elements/header/header/type';
import VoucherList from '@/components/sections/builder/elements/header/header/cart-dropdown/voucher-list';
import Cookies from 'js-cookie';
import CartList from '@/components/sections/builder/elements/header/header/cart-dropdown/cart-list';
import { useGetListPromotions, useMutateResetPromotion } from '@/hooks/api/storefront/promotion';
import { BasePromotion } from '@/types/http/promotion/common';
import { usePromotionStore } from '@/stores/promotion';
import { shallow } from 'zustand/shallow';
import ModalAlert from '@/components/sections/modal-alert';
import { toast } from '@/components/ui/use-toast';
import ActiveOffersList from '@/components/sections/builder/elements/header/header/cart-dropdown/active-offers-list';
import { dummyCarts } from '@/constant/dummyData/dummy-carts';
import { queryClient } from '@/lib/query-client';
import GiftWizard from '@/components/sections/builder/elements/header/header/cart-dropdown/gift-wizard';
import { useFreeGiftWizard } from '@/hooks/use-free-gift-wizard';
import {
  useAutoApplyBestSpendingPromo,
  useAutoApplySpecificFreeGift,
  usePruneSpendingPromos,
} from '@/hooks/use-promotion';
import { buildAutoFreeGifts, ensureApplied } from '@/lib/promotions';

export interface CartDrawerProps {
  openModal?: boolean;
  cart: HttpTypes.StoreCart;
  setOpenModal?: (open: boolean) => void;
  host?: string;
  theme: ThemeType;
  preview?: boolean;
  isLoadingData?: boolean;
}

export function CartDrawer({
  openModal,
  setOpenModal,
  cart,
  host,
  theme,
  preview,
  isLoadingData,
}: CartDrawerProps) {
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const token = preview ? query.get('token') : Cookies.get('accessToken') || '';
  const locationId = Cookies.get('_location_id') || '';
  const modalOverlap = useModal();
  const wizard = useFreeGiftWizard();

  const [currentItemQty, setCurrentItemQty] = useState<{ [key: string]: number }>({});
  const [drawerView, setDrawerView] = useState<'cart' | 'voucher' | 'gift' | 'active_offers'>(
    'cart'
  );
  const [voucherCode, setVoucherCode] = useState<string>('');
  const [errorVoucher, setErrorVoucher] = useState<string | null>(null);
  const [pendingVoucher, setPendingVoucher] = useState<{
    promoRewards: BasePromotion[];
    code: string;
  } | null>(null);

  const pendingQtyRef = useRef<{ [key: string]: number }>({});
  const originalQtyRef = useRef<{ [key: string]: number }>({});

  const isQtyRefEmpty =
    Object.keys(pendingQtyRef.current).length === 0 &&
    Object.keys(originalQtyRef.current).length === 0;

  const cartItems = preview ? dummyCarts.items : cart?.items;
  const isEmptyCart = cartItems?.length === 0;

  const {
    selectedPromotion,
    removePromotion,
    replaceFreeGift,
    removeFreeGift,
    syncFreeGiftPromotion,
    applyVoucher,
    applyVoucherRewards,
  } = usePromotionStore(
    (state) => ({
      selectedPromotion: state.selectedPromotion,
      removePromotion: state.removePromotion,
      replaceFreeGift: state.replaceFreeGift,
      removeFreeGift: state.removeFreeGift,
      syncFreeGiftPromotion: state.syncFreeGiftPromotion,
      applyVoucher: state.applyVoucher,
      applyVoucherRewards: state.applyVoucherRewards,
    }),
    shallow
  );

  const getEffectiveQty = (itemId: string, fallback: number) => currentItemQty[itemId] ?? fallback;

  const subtotal = useMemo(() => {
    if (!cartItems?.length) return 0;

    return cartItems.reduce((total, item) => {
      const qty = getEffectiveQty(item.id, item.quantity);
      const price = Number(
        Number(item.discount_price) > 0 && Number(item.discount_price) < Number(item.sell_price)
          ? item.discount_price
          : item.sell_price
      );
      return total + price * qty;
    }, 0);
  }, [cartItems, currentItemQty]);

  const payloadListPromotion = useMemo(() => {
    const items = (cartItems as BaseCart[]) || [];

    const { mappedItems, grandTotal } = items.reduce(
      (acc, item) => {
        const discountPrice = item.discount_price ?? 0;
        const discountAmount = item.discount_amount ?? 0;
        const sellPrice = item.sell_price ?? 0;

        const pricePerItem = discountPrice > 0 ? discountPrice : sellPrice;
        const qty = getEffectiveQty(item.id, item.quantity);
        const itemTotal = qty * pricePerItem;

        acc.mappedItems.push({
          item_id: item.id,
          qty,
          amount: itemTotal,
        });

        // NOTE: kalau discount_amount itu per-item, kalikan qty
        const effectiveDiscount = discountAmount > 0 ? discountAmount : 0;
        acc.grandTotal += itemTotal - effectiveDiscount;

        return acc;
      },
      {
        mappedItems: [] as { item_id: string; qty: number; amount: number }[],
        grandTotal: 0,
      }
    );

    return {
      items: mappedItems,
      grand_total: grandTotal,
      current_date: new Date().toISOString(),
    };
  }, [cartItems, currentItemQty]);

  const { data: recommendPromotion, isLoading: isLoadingRecommend } = useGetListPromotions(
    payloadListPromotion,
    true,
    token as string,
    {
      enabled: !!payloadListPromotion.items?.length && openModal && isQtyRefEmpty,
    }
  );

  const { data: listPromotion, isLoading: isLoadingPromotions } = useGetListPromotions(
    payloadListPromotion,
    false,
    token as string,
    {
      enabled: !!payloadListPromotion.items.length && openModal && isQtyRefEmpty,
    }
  );

  const allPromotions = useMemo(() => {
    const selectedIds = new Set(selectedPromotion.map((promo) => promo.promotion_id));
    const merged = [...selectedPromotion];

    listPromotion?.forEach((promo) => {
      if (!selectedIds.has(promo.promotion_id)) {
        merged.push(promo);
      }
    });

    return merged;
  }, [listPromotion, selectedPromotion]);

  const handleApplyVoucher = useCallback(
    (voucherIds: string[]) => {
      if (!allPromotions) return;

      const selected = allPromotions.filter((p) => voucherIds.includes(p.promotion_id));
      const hasNonOverlap = selected.some((p) => p.is_overlap === false);
      const finalPromos = hasNonOverlap ? selected : selected.filter((p) => p.is_overlap === true);

      const prevIds = new Set(selectedPromotion.map((p) => p.promotion_id));
      const nextIds = new Set(finalPromos.map((p) => p.promotion_id));

      applyVoucher(voucherIds, allPromotions);

      prevIds.forEach((id) => {
        if (!nextIds.has(id)) {
          removeFreeGift(id);
        }
      });

      for (const promo of finalPromos) {
        const hasApplied = (promo.applied_product_reward?.length ?? 0) > 0;
        const hasInclude = (promo.product_reward ?? []).some(
          (r) => r.reward_type === 'product' && r.rule_type === 'include'
        );
        if (!hasApplied || !hasInclude) continue;

        const gifts = buildAutoFreeGifts(promo);
        if (gifts.length > 0) {
          replaceFreeGift(promo.promotion_id, gifts, 'replace');
        }
      }

      syncFreeGiftPromotion();
    },
    [
      allPromotions,
      selectedPromotion,
      applyVoucher,
      removeFreeGift,
      replaceFreeGift,
      syncFreeGiftPromotion,
    ]
  );

  const detectPromotionConflict = (existingPromos: BasePromotion[], newPromos: BasePromotion[]) => {
    const hasExistingNonOverlap = existingPromos.some((p) => !p.is_overlap);
    const hasNewNonOverlap = newPromos.some((p) => !p.is_overlap);
    const allExistingOverlap = existingPromos.every((p) => p.is_overlap);

    return (allExistingOverlap && hasNewNonOverlap) || (hasExistingNonOverlap && !hasNewNonOverlap);
  };

  const applyGiftsForActivePromos = (
    activePromos: BasePromotion[],
    sourcePromos: BasePromotion[]
  ) => {
    for (const promo of activePromos) {
      const enriched = ensureApplied(promo, sourcePromos);

      const hasInclude = (enriched.product_reward ?? []).some(
        (r) => r.reward_type === 'product' && r.rule_type === 'include'
      );

      if (!hasInclude) {
        removeFreeGift(enriched.promotion_id);
        continue;
      }

      const gifts = buildAutoFreeGifts(enriched);

      if (gifts.length > 0) {
        replaceFreeGift(enriched.promotion_id, gifts as any, 'replace');
      } else {
        removeFreeGift(enriched.promotion_id);
      }
    }
    syncFreeGiftPromotion();
  };

  const handleSubmitRedeemVoucher = (promoRewards: BasePromotion[], code: string) => {
    const isNewOverlapFalse = promoRewards.some((promo) => promo.is_overlap === false);

    const hasConflict = detectPromotionConflict(selectedPromotion || [], promoRewards);

    if (hasConflict) {
      setPendingVoucher({ promoRewards, code });
      modalOverlap.setIsShow(true);
      return;
    }

    applyVoucherRewards(promoRewards, code, isNewOverlapFalse);
  };

  const handleConfirmOverlap = useCallback(() => {
    if (!pendingVoucher) return;
    const { promoRewards, code } = pendingVoucher;

    const prevIds = new Set(selectedPromotion.map((p) => p.promotion_id));

    const isNewOverlapFalse = promoRewards.some((p) => p.is_overlap === false);
    applyVoucherRewards(promoRewards, code, isNewOverlapFalse);

    const nextSelected = promoRewards;
    const nextIds = new Set(nextSelected.map((p) => p.promotion_id));

    prevIds.forEach((id) => {
      if (!nextIds.has(id)) {
        removeFreeGift(id);
      }
    });

    const sources = [...promoRewards, ...(listPromotion ?? [])];
    applyGiftsForActivePromos(nextSelected, sources);

    modalOverlap.setIsShow(false);
    setPendingVoucher(null);
    setDrawerView('cart');

    toast({
      title: 'Success',
      variant: 'success',
      description: 'Promotion has been applied',
      duration: 3000,
    });
  }, [setPendingVoucher, pendingVoucher, modalOverlap, listPromotion]);

  const handleRemovePromotion = (promoId: string) => {
    removePromotion(promoId);
  };

  useEffect(() => {
    if (cartItems?.length) {
      setCurrentItemQty(Object.fromEntries(cartItems.map((i) => [i.id, i.quantity])));
    } else {
      setCurrentItemQty({});
    }
  }, [cartItems]);

  usePruneSpendingPromos({
    listPromotion,
    grandTotal: payloadListPromotion.grand_total,
    preview,
    isLoadingData: isLoadingData || isLoadingRecommend || isLoadingPromotions,
  });

  useAutoApplySpecificFreeGift({
    listPromotion,
    cartItems: payloadListPromotion.items,
    grandTotal: payloadListPromotion.grand_total,
    preview,
    isLoadingData: isLoadingData || isLoadingRecommend || isLoadingPromotions,
  });

  useAutoApplyBestSpendingPromo({
    listPromotion,
    recommendPromotion,
    grandTotal: payloadListPromotion.grand_total,
    preview,
    isLoadingData: isLoadingData || isLoadingRecommend || isLoadingPromotions,
    skipIfHasNonOverlapSelected: true,
  });

  useEffect(() => {
    syncFreeGiftPromotion();
  }, [selectedPromotion]);

  return (
    <>
      <Drawer
        open={openModal}
        onOpenChange={(isOpen) => {
          setOpenModal?.(isOpen);
          if (!isOpen) {
            setDrawerView('cart');
          }
        }}
        direction='right'
      >
        <DrawerContent className='theme_cart_drawer_bg theme_font theme_drawer fixed inset-y-0 right-0 z-[106] flex h-auto w-full flex-col border sm:max-w-[28rem]'>
          {drawerView === 'voucher' ? (
            <VoucherList
              cart={cart}
              promotionTheme={theme?.promotion}
              drawerView={drawerView}
              setDrawerView={setDrawerView}
              openModal={openModal as boolean}
              token={token as string}
              listPromotions={allPromotions}
              isLoadingListPromotions={isLoadingPromotions}
              payloadListPromotion={payloadListPromotion}
              voucherCode={voucherCode}
              setVoucherCode={setVoucherCode}
              setErrorVoucher={setErrorVoucher}
              errorVoucher={errorVoucher}
              onApplyVoucher={handleApplyVoucher}
              onRedeemVoucher={handleSubmitRedeemVoucher}
            />
          ) : drawerView === 'cart' ? (
            <>
              {isEmptyCart ? (
                <>
                  <DrawerHeader className='flex items-center justify-between text-left'>
                    <DrawerTitle />
                    <DrawerClose>
                      <XIcon className='theme_cart_drawer_color h-4 w-4' />
                    </DrawerClose>
                  </DrawerHeader>
                  <div className='flex h-full items-center justify-center px-4'>
                    <div className='theme_cart_drawer_color text-xl'>Your cart is empty</div>
                  </div>
                </>
              ) : (
                <CartList
                  cart={cart}
                  cartItems={cartItems as BaseCart[]}
                  setDrawerView={setDrawerView}
                  setOpenModal={(value) =>
                    setOpenModal?.(typeof value === 'boolean' ? value : value(false))
                  }
                  token={token as string}
                  subtotal={subtotal}
                  setCurrentItemQty={setCurrentItemQty}
                  currentItemQty={currentItemQty}
                  host={host as string}
                  themeId={themeId as string}
                  theme={theme}
                  isPreview={preview}
                  recommendPromotion={recommendPromotion}
                  isLoadingRecommend={isLoadingRecommend}
                  payloadListPromotion={payloadListPromotion}
                  colorScheme={theme?.promotion?.colorScheme}
                  wizard={wizard}
                  isLoadingData={isLoadingData || isLoadingRecommend || isLoadingPromotions}
                  pendingQtyRef={pendingQtyRef}
                  originalQtyRef={originalQtyRef}
                />
              )}
            </>
          ) : drawerView === 'gift' ? (
            <GiftWizard
              wizard={wizard}
              token={token as string}
              locationId={locationId}
              colorScheme={theme?.promotion?.colorScheme}
              setDrawerView={setDrawerView}
            />
          ) : (
            <ActiveOffersList
              colorSchemeCart={theme?.cart?.drawerColorScheme}
              promotionTheme={theme?.promotion}
              setDrawerView={setDrawerView}
              listPromotions={selectedPromotion}
              grandTotal={payloadListPromotion.grand_total}
              onRemovePromotion={(promo) => handleRemovePromotion(promo.promotion_id)}
            />
          )}
        </DrawerContent>
      </Drawer>

      <ModalAlert
        openModal={modalOverlap.isShow}
        setOpenModal={modalOverlap.setIsShow}
        title='Are you sure'
        description='These vouchers cannot overlap with existing promotions, do you want to replace them?'
        onConfirm={handleConfirmOverlap}
      />
    </>
  );
}

export function CartPopup({
  openModal,
  setOpenModal,
  cart,
  host,
}: {
  openModal?: boolean;
  cart: BaseCart | null;
  setOpenModal?: (open: boolean) => void;
  host: string;
}) {
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const token = query.get('token') || '';

  const { data: carts } = useQuery({
    queryKey: ['carts'],
    queryFn: async () => {
      const carts = await retrieveCart(host as string);
      return carts?.data;
    },
  });
  const [currentItemQty] = React.useState<{ [key: string]: number }>({});
  const [subtotal, setSubtotal] = React.useState<number>(0);

  const cartItems = carts?.items;

  React.useEffect(() => {
    const newSubtotal = cartItems?.reduce((total, item) => {
      const qty = currentItemQty[item.id] || item.quantity;
      return total + (item.discount_price ? item.discount_price : item.sell_price) * qty;
    }, 0);
    setSubtotal(newSubtotal || 0);
  }, [currentItemQty, cartItems]);

  const images = cart?.product_assets[0];
  const displayedQuantity = cart?.quantity || 0;
  const totalPrice = displayedQuantity * (cart?.sell_price || 0);

  return (
    <Drawer open={openModal} onOpenChange={setOpenModal} direction='top'>
      <DrawerContent className='theme_cart_drawer_bg theme_font fixed inset-x-0 top-0 z-50 flex h-auto w-full flex-col transition-all duration-1000'>
        {cart ? (
          <div className='theme_cart_drawer_color mx-2 px-4 pb-6 pt-2'>
            <Table>
              <TableHeader>
                <TableRow className='hover:bg-inherit'>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Baru Saja Ditambahkan
                  </TableHead>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Barang
                  </TableHead>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Jumlah
                  </TableHead>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Total Harga
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className='hover:bg-inherit'>
                  <TableCell>
                    <div className='w-44'>
                      <Image
                        src={typeof images === 'object' ? images?.assets_url : images || ''}
                        width={100}
                        height={100}
                        className='h-auto w-full object-cover'
                        alt={cart.product_name}
                      />
                    </div>
                  </TableCell>
                  <TableCell>{cart.product_name}</TableCell>
                  <TableCell>{cart.quantity}</TableCell>
                  <TableCell>{formatIDR(totalPrice || 0)}</TableCell>
                  <TableCell>
                    <div className='flex flex-col items-end text-end'>
                      <p>{formatIDR(subtotal)}</p>
                      <span className='text-xs font-semibold italic'>
                        *Belum Termasuk Biaya Pengiriman{' '}
                      </span>
                      <div className='flex gap-1 '>
                        <DrawerClose>
                          <Button type='button'>Continue Shopping</Button>
                        </DrawerClose>
                        <Button type='button' variant='outline'>
                          <Link href={`/${token ? `?theme_id=${themeId}&token=${token}` : ''}cart`}>
                            Cart
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

export function CartDropdownButton({
  preview,
  cart,
  host,
  theme,
  content,
  isLoadingData,
}: {
  preview?: boolean;
  cart: HttpTypes.StoreCart;
  host: string;
  theme?: ThemeType;
  content?: HeaderElementDataType;
  isLoadingData?: boolean;
}) {
  const cartDrawer = useModal();
  const cartItems = cart?.items;
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const cartIdCookie = Cookies.get('_jstore_cart_id');
  const token = Cookies.get('accessToken');

  const { clearSelectedPromotions } = usePromotionStore(
    (state) => ({
      clearSelectedPromotions: state.clearSelectedPromotions,
    }),
    shallow
  );

  const resetPromotion = useMutateResetPromotion({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      clearSelectedPromotions();
    },
  });

  const totalItems =
    cartItems?.reduce((acc, item) => {
      return acc + item.quantity;
    }, 0) || 0;

  const backgroundColor = content?.colorSchemeHeader?.background || '#ffffff';
  const isDark = React.useMemo(() => isDarkColor(backgroundColor), [backgroundColor]);

  useEffect(() => {
    const cartId = cartIdCookie || cart?.cartId;
    if (cartId && (cart?.promotions?.length ?? 0) > 0 && cartDrawer.isShow) {
      resetPromotion.mutate({ cartId, token });
    }
  }, [cartIdCookie, cart?.cartId, cart?.promotions?.length, cartDrawer.isShow, token]);

  const themeCart = theme?.cart;

  return (
    <>
      {themeCart?.cartType !== 'drawer' ? (
        <Link
          prefetch={false}
          href={{
            pathname: '/cart',
            query: preview ? { isPreview: 'true', theme_id: themeId ?? '' } : undefined,
          }}
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'icon' }),
            'inline-flex items-center justify-center'
          )}
        >
          <span className='relative inline-flex'>
            <ShoppingBag className='h-6 w-6' data-test='cart-icon' />
            {cartItems && cartItems?.length > 0 && (
              <span
                className={cn(
                  'absolute -bottom-2 flex h-[18px] w-[18px] items-center justify-center rounded-full p-0.5 text-[9px]',
                  isDark ? 'bg-white text-black' : 'bg-black text-white'
                )}
                aria-label='Cart Item Count'
              >
                {totalItems > 99 ? '99+' : totalItems}
              </span>
            )}
          </span>
        </Link>
      ) : (
        <>
          <Button
            variant='ghost'
            size='icon'
            onClick={() => cartDrawer.open()}
            className='theme_cart_drawer_button'
          >
            <div className='relative'>
              <ShoppingBag className='h-6 w-6' />
              {cartItems && cartItems.length > 0 && (
                <div
                  className={cn(
                    'absolute -bottom-2 flex h-[18px] w-[18px] items-center justify-center rounded-full p-0.5 text-[9px]',
                    isDark ? 'bg-white text-black' : 'bg-black text-white'
                  )}
                >
                  {totalItems > 99 ? '99+' : totalItems}
                </div>
              )}
            </div>
          </Button>
          <CartDrawer
            openModal={cartDrawer.isShow}
            setOpenModal={cartDrawer.setIsShow}
            cart={cart}
            host={host}
            theme={theme as ThemeType}
            preview={preview}
            isLoadingData={isLoadingData}
          />
        </>
      )}
    </>
  );
}

function isDarkColor(hex: string): boolean {
  if (!hex) return false;

  const rgb = hex.match(/\w\w/g)?.map((x) => parseInt(x, 16));
  if (!rgb) return false;

  const [r, g, b] = rgb;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luminance < 128;
}
